import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { normalizeLayerUrl } from "../../utils/serviceRoot";
import "./styles/ResultsTable.css";

export type ColumnDef = { field: string; label: string; hidden?: boolean; layer?: string; };

type SortDir = "asc" | "desc" | null;

type Props = {
    rows: any[];
    columns: ColumnDef[];
    selectedIndex?: number;
    onRowClick?: (index: number) => void;
    footer?: { rangeText: string; page: number; pageSize: number };
    showColumnChooser?: boolean;
    defaultPageSize?: number;
    pageSizeOptions?: number[];
    activeServiceRoot?: string | null;
};

const MENU_MIN_MARGIN = 8;

const ResultsTable: React.FC<Props> = ({
    rows,
    columns,
    selectedIndex = -1,
    onRowClick,
    footer,
    showColumnChooser = true,
    defaultPageSize,
    pageSizeOptions,
    activeServiceRoot
}) => {
    const initialVisible = (cols: ColumnDef[], activeLayerUrl: string | null) => {
        const active = activeLayerUrl ? normalizeLayerUrl(activeLayerUrl) : null;
        const next: Record<string, boolean> = {};
        let matchedAny = false;

        for (const c of cols) {
            if (!c.layer) {
                next[c.field] = !(c.hidden ?? false);
            } else {
                const colLayer = normalizeLayerUrl(c.layer);
                const isMatch = !!active && colLayer === active;
                next[c.field] = active ? isMatch : !(c.hidden ?? false);
                if (isMatch) matchedAny = true;
            }
        }
        // revert to the default visibility (non-hidden) so we don't hide everything.
        if (active && !matchedAny) {
            for (const c of cols) {
                next[c.field] = !(c.hidden ?? false);
            }
        }

        return next;
    };

    const [visible, setVisible] = useState<Record<string, boolean>>(
        () => Object.fromEntries(columns.map((c) => [c.field, !c.hidden]))
    );

    const options = (pageSizeOptions && pageSizeOptions.length ? pageSizeOptions : [5, 10, 50, 100, 500, 1000]);
    const initialPageSize = (defaultPageSize && options.includes(defaultPageSize))
        ? defaultPageSize
        : (footer?.pageSize && options.includes(footer.pageSize) ? footer.pageSize : options[0]);

    const [pageSize, setPageSize] = useState<number>(initiallyValid(initialPageSize, options));
    const [page, setPage] = useState<number>(1);

    useEffect(() => {
        setPageSize(prev => (options.includes(prev) ? prev : options[0]));
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.join(","), defaultPageSize]);

    useEffect(() => { setPage(1); }, [rows, pageSize]);

    const total = rows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startIndex = total ? (page - 1) * pageSize : 0;
    const endIndex = Math.min(total, startIndex + pageSize);
    const rangeText = total ? `${startIndex + 1} - ${endIndex} of ${total} results` : "0 results";

    const shownColumns = useMemo(
        () => columns.filter((c) => visible[c.field]),
        [columns, visible]
    );

    const toggle = (field: string) =>
        setVisible((v) => ({ ...v, [field]: !v[field] }));

    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>(null);

    const cycleSort = (field: string) => {
        if (sortField !== field) {
            setSortField(field);
            setSortDir("asc");
        } else {
            setSortDir((d) => (d === "asc" ? "desc" : d === "desc" ? null : "asc"));
            if (sortDir === "desc") setSortField(null);
        }
        setPage(1);
    };

    const keyActivateHeader = (e: React.KeyboardEvent, field: string) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            cycleSort(field);
        }
    };

    const normalized = (v: any) => {
        if (v == null) return null;
        if (typeof v === "number") return v;
        const n = Number(v);
        if (!Number.isNaN(n) && String(v).trim() !== "") return n;
        return String(v).toLowerCase();
    };

    const sortedRows = useMemo(() => {
        if (!sortField || !sortDir) return rows;
        const copy = [...rows];
        copy.sort((a, b) => {
            const av = normalized(a[sortField]);
            const bv = normalized(b[sortField]);
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (av < bv) return sortDir === "asc" ? -1 : 1;
            if (av > bv) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return copy;
    }, [rows, sortField, sortDir]);

    const pagedRows = useMemo(
        () => sortedRows.slice(startIndex, endIndex),
        [sortedRows, startIndex, endIndex]
    );

    const canPrev = page > 1;
    const canNext = page < totalPages;

    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const placeMenuRough = () => {
        const btn = buttonRef.current;
        if (!btn) return;
        const r = btn.getBoundingClientRect();
        const assumedWidth = 260;
        const left = Math.max(
            MENU_MIN_MARGIN,
            Math.min(r.right - assumedWidth, window.innerWidth - assumedWidth - MENU_MIN_MARGIN)
        );
        const top = Math.min(r.bottom + 6, window.innerHeight - 200);
        setMenuPos({ top, left });
    };

    const placeMenuPrecise = () => {
        const btn = buttonRef.current;
        const menu = menuRef.current;
        if (!btn || !menu) return;
        const r = btn.getBoundingClientRect();
        const mw = menu.offsetWidth;
        const mh = menu.offsetHeight;
        const maxLeft = window.innerWidth - mw - MENU_MIN_MARGIN;
        const maxTop = window.innerHeight - mh - MENU_MIN_MARGIN;
        const left = Math.max(MENU_MIN_MARGIN, Math.min(r.right - mw, maxLeft));
        const top = Math.max(MENU_MIN_MARGIN, Math.min(r.bottom + 6, maxTop));
        const fallbackLeft = Math.max(MENU_MIN_MARGIN, Math.min(r.left, maxLeft));
        setMenuPos({ top, left: left < MENU_MIN_MARGIN ? fallbackLeft : left });
    };

    useEffect(() => {
        if (!menuOpen) return;
        placeMenuRough();
        const onOutside = (e: MouseEvent) => {
            if (!menuRef.current || !buttonRef.current) return;
            const t = e.target as Node;
            if (!menuRef.current.contains(t) && !buttonRef.current.contains(t)) setMenuOpen(false);
        };
        const onAnyScroll = () => placeMenuPrecise();
        const onResize = () => placeMenuPrecise();

        document.addEventListener("mousedown", onOutside);
        window.addEventListener("scroll", onAnyScroll, true);
        window.addEventListener("resize", onResize);
        const id = window.requestAnimationFrame(placeMenuPrecise);
        return () => {
            document.removeEventListener("mousedown", onOutside);
            window.removeEventListener("scroll", onAnyScroll, true);
            window.removeEventListener("resize", onResize);
            window.cancelAnimationFrame(id);
        };
    }, [menuOpen]);

    useEffect(() => {
        setVisible(prev => initialVisible(columns, activeServiceRoot ?? null));
    }, [activeServiceRoot, columns]);

    return (
        <div className="results-table">
            <div className="results-table__title">Search Results</div>

            {showColumnChooser && menuOpen &&
                ReactDOM.createPortal(
                    <div
                        ref={menuRef}
                        className="results-table__cols-menu"
                        style={{ top: menuPos.top, left: menuPos.left }}
                    >
                        {columns.map((col) => (
                            <label key={col.field} className="cols-menu__row">
                                <input
                                    type="checkbox"
                                    checked={!!visible[col.field]}
                                    onChange={() => toggle(col.field)}
                                />
                                <span>{col.label}</span>
                            </label>
                        ))}
                    </div>,
                    document.body
                )}

            <div className="results-table__body">
                <div className="results-table__scroller" role="region" aria-label="Search results">
                    <table className="results-table__table">
                        <thead>
                            <tr>
                                {shownColumns.map((col) => {
                                    const isActive = sortField === col.field && !!sortDir;
                                    return (
                                        <th
                                            key={col.field}
                                            onClick={() => cycleSort(col.field)}
                                            onKeyDown={(e) => keyActivateHeader(e, col.field)}
                                            role="button"
                                            tabIndex={0}
                                            aria-sort={
                                                sortField !== col.field || !sortDir
                                                    ? "none"
                                                    : sortDir === "asc" ? "ascending" : "descending"
                                            }
                                            className="th--sortable"
                                            title="Click to sort"
                                        >
                                            <span className="th__inner">
                                                <span className="th__label">{col.label}</span>
                                                {isActive && (
                                                    <span
                                                        className={`sort-caret ${sortDir === "asc" ? "sort-caret--asc" : "sort-caret--desc"}`}
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>

                        <tbody>
                            {pagedRows.map((r, i) => {
                                const globalIdx = startIndex + i;
                                const isSel = globalIdx === selectedIndex;
                                return (
                                    <tr
                                        key={globalIdx}
                                        className={isSel ? "is-selected" : undefined}
                                        onClick={() => onRowClick?.(globalIdx)}
                                    >
                                        {shownColumns.map((col) => (
                                            <td key={col.field} className="cell--wrap">
                                                {r[col.field] ?? ""}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {showColumnChooser && (
                    <div className="results-table__side">
                        <button
                            ref={buttonRef}
                            className="results-table__cols-btn"
                            aria-label="Show/hide columns"
                            onClick={() => setMenuOpen((v) => !v)}
                        >
                            ⋮
                        </button>
                    </div>
                )}
            </div>

            <div className="results-table__footer">
                <div className="footer__left">{rangeText}</div>
                <div className="footer__right">
                    <button className="pager-btn" onClick={() => setPage(1)} disabled={!canPrev}>«</button>
                    <button className="pager-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={!canPrev}>‹</button>
                    <span className="pager-page">{page}</span>
                    <button className="pager-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={!canNext}>›</button>
                    <button className="pager-btn" onClick={() => setPage(totalPages)} disabled={!canNext}>»</button>
                    <select
                        className="pager-size"
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                        {options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

function initiallyValid(value: number, opts: number[]) {
    return opts.includes(value) ? value : (opts[0] ?? 5);
}

export default ResultsTable;
